import { createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { FCX, getEnding } from "@datassential/platform-ui-lib";
import { KeywordBase } from "@api/useGetKeywordBaseData";
import { toggleInArray } from "@core/utils/array";
import { toast } from "react-toastify";

export type KEYWORD_ID = KeywordBase['id'];

export type SelectedKeywordsContextData = {
    selectedKeywordsIds: KEYWORD_ID[];
    setSelectedKeywordsIds: Dispatch<SetStateAction<KEYWORD_ID[]>>;
    toggleKeywordSelection: (id: KEYWORD_ID) => void;
    isSelectedKeywordsView: boolean;
    setIsSelectedKeywordsView: Dispatch<SetStateAction<boolean>>;
};

const DEFAULTS: SelectedKeywordsContextData = {
    selectedKeywordsIds: [],
    setSelectedKeywordsIds: () => null,
    toggleKeywordSelection: () => null,
    isSelectedKeywordsView: false,
    setIsSelectedKeywordsView: () => null,
}

const SelectedKeywordsContext = createContext<SelectedKeywordsContextData>(DEFAULTS);

export function useSelectedKeywordsContext() {
    return useContext(SelectedKeywordsContext);
}

export function useFilterOutSelectedKeywords<T extends KeywordBase>(rawData: T[]) {
    const {
        selectedKeywordsIds,
        setSelectedKeywordsIds,
    } = useSelectedKeywordsContext();

    useEffect(() => {
        if (rawData.length > 0) {
            const unselectedIds = selectedKeywordsIds.filter(i => !rawData.some(d => d.id === i));
            if (unselectedIds.length > 0) {
                setSelectedKeywordsIds(prev => prev.filter(i => !unselectedIds.includes(i)));
                toast.info(`${unselectedIds.length} ${getEnding('item', unselectedIds.length)} ${unselectedIds.length === 1 ? 'was' : 'were'} unselected.`)
            }
        }
    }, [rawData, selectedKeywordsIds, setSelectedKeywordsIds]);
}

export const SelectedKeywordsProvider: FCX = ({
    children,
}) => {
    const [value, setValue] = useState<SelectedKeywordsContextData['selectedKeywordsIds']>(DEFAULTS['selectedKeywordsIds']);
    const [isViewEnabled, setIsViewEnabled] = useState<SelectedKeywordsContextData['isSelectedKeywordsView']>(DEFAULTS['isSelectedKeywordsView']);

    const toggle = useCallback(
        (id: KEYWORD_ID) => setValue(prev => {
            const result = toggleInArray(prev, id);

            if (result.length === 0) {
                setIsViewEnabled(false);
            }

            return result;
        }),
        [setValue, setIsViewEnabled]
    );

    const contextValue = useMemo(
        (): SelectedKeywordsContextData => ({
            selectedKeywordsIds: value,
            setSelectedKeywordsIds: setValue,
            toggleKeywordSelection: toggle,
            isSelectedKeywordsView: isViewEnabled,
            setIsSelectedKeywordsView: setIsViewEnabled,
        }),
        [value, setValue, toggle, isViewEnabled, setIsViewEnabled]
    );

    return (
        <SelectedKeywordsContext.Provider value={contextValue}>
            {children}
        </SelectedKeywordsContext.Provider>
    );
};